// ╔══════════════════════════════════════════════════════════════╗
// ║  الرفع — Excel uploads hub                                    ║
// ║  Tabs: employees · advances · bonuses · deductions            ║
// ║  (Fingerprint upload lives on its own /fingerprint page —     ║
// ║   used by the dedicated `att` role.)                           ║
// ╚══════════════════════════════════════════════════════════════╝

import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api.js';
import { useToast } from '../components/Toast.jsx';
import { fmtAR } from '../lib/format.js';
import { Badge, Btn, Card } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';

const UPLOAD_TYPES = [
  {
    id: 'employees',
    title: 'بيانات الموظفين',
    description:
      'ملف Excel فيه بيانات الموظفين الأساسية (الكود + الاسم + الإدارة + الراتب). الكود لازم يطابق الكود فى جهاز البصمة.',
    endpoint: '/employees/import',
    icon: 'users',
    sampleColumns: ['الكود', 'الاسم', 'الاسم انجليزي', 'الإدارة', 'الوظيفة', 'رقم التليفون', 'الراتب', 'الرقم القومي', 'تاريخ التعيين', 'العنوان'],
  },
  {
    id: 'advances',
    title: 'السلف',
    description:
      'استيراد سلف بالجملة لعدة موظفين دفعة واحدة. كل سطر = سلفة جديدة.',
    endpoint: '/advances/import',
    icon: 'briefcase',
    sampleColumns: ['رقم الموظف', 'المبلغ', 'الأقساط', 'السبب', 'التاريخ'],
  },
  {
    id: 'bonuses',
    title: 'المكافآت',
    description: 'استيراد مكافآت إضافية (غير إنتاجية) لعدة موظفين دفعة واحدة.',
    endpoint: '/bonuses/import',
    icon: 'award',
    sampleColumns: ['رقم الموظف', 'المبلغ', 'النوع', 'الشهر', 'ملاحظة'],
  },
  {
    id: 'deductions',
    title: 'الخصومات',
    description: 'استيراد خصومات إضافية (تأخيرات أو غياب أو مخالفات) لعدة موظفين.',
    endpoint: '/deductions/import',
    icon: 'trendD',
    sampleColumns: ['رقم الموظف', 'المبلغ', 'النوع', 'الشهر', 'ملاحظة'],
  },
];

export default function UploadsPage() {
  const [activeTab, setActiveTab] = useState('employees');
  const current = UPLOAD_TYPES.find((t) => t.id === activeTab);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
          الرفع والاستيراد
        </div>
        <h1 className="text-2xl md:text-3xl font-black mt-1">رفع ملفات Excel</h1>
        <p className="text-sm text-ink-400 mt-1">
          استيراد بيانات الموظفين والسلف والمكافآت والخصومات بالجملة من ملفات Excel.
          (رفع البصمة له صفحة مستقلّة <a className="text-brand-500 underline" href="/fingerprint">/fingerprint</a>.)
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-ink-100 dark:border-ink-800 overflow-x-auto no-scrollbar">
        {UPLOAD_TYPES.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 h-12 text-sm font-semibold border-b-2 -mb-px transition whitespace-nowrap inline-flex items-center gap-2 ${
              activeTab === t.id
                ? 'border-brand-500 text-brand-600 dark:text-brand-300'
                : 'border-transparent text-ink-400 hover:text-ink-700 dark:hover:text-ink-100'
            }`}
          >
            <Icon name={t.icon} size={14} />
            {t.title}
          </button>
        ))}
      </div>

      {/* Active uploader */}
      <Uploader key={current.id} {...current} />
    </div>
  );
}

function Uploader({ id, title, description, endpoint, icon, sampleColumns }) {
  const toast = useToast();
  const fileInput = useRef(null);
  const [drag, setDrag] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const previewMut = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(`${endpoint}?preview=true`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (res) => {
      setPreviewData(res.data.summary);
    },
    onError: (err) => {
      setPendingFile(null);
      setPreviewData(null);
      toast.push({
        tone: 'danger',
        title: 'فشل قراءة الملف',
        body: getErrorMessage(err),
      });
    },
  });

  const confirmMut = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(endpoint, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (res) => {
      const s = res.data.summary;
      setLastResult(s);
      setPendingFile(null);
      setPreviewData(null);
      toast.push({
        tone: 'success',
        title: 'تم الاستيراد بنجاح',
        body: `${s.inserted ?? 0} مدخل جديد · ${s.skipped ?? 0} متجاهل`,
      });
    },
    onError: (err) => {
      toast.push({
        tone: 'danger',
        title: 'فشل الاستيراد',
        body: getErrorMessage(err),
      });
    },
  });

  function handleFile(f) {
    if (!f) return;
    setLastResult(null);
    setPendingFile(f);
    previewMut.mutate(f);
  }

  function handleConfirm() {
    if (pendingFile) confirmMut.mutate(pendingFile);
  }

  function handleCancel() {
    setPendingFile(null);
    setPreviewData(null);
    previewMut.reset();
  }

  const showingPreview = !!previewData && !!pendingFile;
  const busy = previewMut.isPending || confirmMut.isPending;

  return (
    <div className="space-y-4">
      <Card className="p-5 sm:p-8">
        <div className="flex items-start gap-4 mb-5">
          <span className="w-12 h-12 rounded-2xl bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 grid place-items-center shrink-0">
            <Icon name={icon} size={22} />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-lg">{title}</h2>
            <p className="text-sm text-ink-400 mt-1">{description}</p>
          </div>
        </div>

        {/* Drop zone — hidden when showing preview */}
        {!showingPreview && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            onClick={() => fileInput.current?.click()}
            className={`relative rounded-2xl border-2 border-dashed cursor-pointer transition p-8 sm:p-12 text-center ${
              drag
                ? 'border-brand-500 bg-brand-50/50 dark:bg-brand-950/30'
                : 'border-ink-200 dark:border-ink-700 bg-ink-50/50 dark:bg-ink-800/30 hover:border-brand-400'
            }`}
          >
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                handleFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            {busy ? (
              <>
                <div className="w-16 h-16 mx-auto rounded-full border-4 border-brand-200 border-t-brand-500 animate-spin" />
                <h3 className="mt-4 font-bold">
                  {confirmMut.isPending ? 'جاري الحفظ…' : 'جاري قراءة الملف…'}
                </h3>
              </>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-brand-300 to-brand-600 grid place-items-center text-ink-950 shadow-pop">
                  <Icon name="upload" size={28} />
                </div>
                <h3 className="mt-4 text-base font-bold">اسحب الملف أو اضغط للاختيار</h3>
                <p className="text-xs text-ink-400 mt-1">.xlsx · .xls · .csv حتى 50MB</p>
                <p className="text-[11px] text-ink-400 mt-2">
                  💡 هتشوف معاينة قبل الحفظ النهائى
                </p>
              </>
            )}
          </div>
        )}

        {/* Preview panel */}
        {showingPreview && (
          <PreviewPanel
            file={pendingFile}
            summary={previewData}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            confirming={confirmMut.isPending}
          />
        )}

        {/* Sample columns */}
        <div className="mt-5 p-4 rounded-xl bg-ink-50 dark:bg-ink-800/40">
          <div className="text-xs font-bold text-ink-500 mb-2">الأعمدة المتوقّعة في الملف:</div>
          <div className="flex flex-wrap gap-1.5">
            {sampleColumns.map((c) => (
              <span
                key={c}
                className="px-2 py-0.5 rounded-md bg-white dark:bg-ink-900 text-xs font-mono"
              >
                {c}
              </span>
            ))}
          </div>
          <p className="text-[10px] text-ink-400 mt-2">
            💡 الأعمدة الإنجليزية مدعومة كمان (مثل: <code>employee_id</code>, <code>amount</code>).
          </p>
        </div>
      </Card>

      {/* Result */}
      {lastResult && (
        <Card className="p-5 ring-1 ring-emerald-300 dark:ring-emerald-700">
          <h3 className="font-bold text-base mb-3 inline-flex items-center gap-2">
            <Icon name="check" size={16} className="text-emerald-500" />
            نتيجة آخر استيراد
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
            <ResultStat label="إجمالي الصفوف" value={lastResult.totalRows ?? 0} tone="neutral" />
            <ResultStat label="مدخل جديد" value={lastResult.inserted ?? 0} tone="success" />
            <ResultStat label="محدّث" value={lastResult.updated ?? 0} tone="info" />
            <ResultStat label="متجاهل" value={lastResult.skipped ?? 0} tone="warn" />
          </div>
          {lastResult.skippedRows?.length > 0 && (
            <details className="mt-4">
              <summary className="text-sm cursor-pointer font-semibold text-amber-700 dark:text-amber-300">
                عرض السطور المتجاهلة ({lastResult.skippedRows.length})
              </summary>
              <ul className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                {lastResult.skippedRows.slice(0, 20).map((r, i) => (
                  <li key={i} className="text-ink-500">
                    صف {r.row}: {r.reason}
                  </li>
                ))}
                {lastResult.skippedRows.length > 20 && (
                  <li className="text-ink-400 italic">… وأكثر</li>
                )}
              </ul>
            </details>
          )}
        </Card>
      )}
    </div>
  );
}

function PreviewPanel({ file, summary, onConfirm, onCancel, confirming }) {
  const ins = summary.inserted ?? 0;
  const upd = summary.updated ?? 0;
  const skp = summary.skipped ?? 0;
  const auto = summary.autoCreated ?? 0;
  const sampleIns = summary.sampleInserts ?? summary.sampleRecords ?? [];
  const sampleUpd = summary.sampleUpdates ?? [];

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 ring-1 ring-amber-200 dark:ring-amber-800">
        <div className="flex items-start gap-3">
          <Icon name="alert" size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-700 dark:text-amber-200">
            <strong>معاينة فقط — لم يتم الحفظ بعد.</strong> راجع البيانات قبل التأكيد.
            <div className="text-[11px] mt-1 opacity-80">الملف: {file?.name}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
        <ResultStat label="إجمالي الصفوف" value={summary.totalRows ?? 0} tone="neutral" />
        <ResultStat label="مدخل جديد" value={ins} tone="success" />
        <ResultStat label={auto > 0 ? 'موظف جديد' : 'محدّث'} value={auto > 0 ? auto : upd} tone="info" />
        <ResultStat label="متجاهل" value={skp} tone="warn" />
      </div>

      {(sampleIns.length > 0 || sampleUpd.length > 0) && (
        <div className="rounded-xl ring-1 ring-ink-100 dark:ring-ink-800 overflow-hidden">
          <div className="px-4 py-2 bg-ink-50 dark:bg-ink-800/40 text-xs font-bold">
            عيّنة من البيانات اللى هتُحفظ ({sampleIns.length + sampleUpd.length})
          </div>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-xs">
              <tbody className="divide-soft">
                {[...sampleIns, ...sampleUpd].map((row, i) => (
                  <tr key={i} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                    {Object.entries(row).map(([k, v]) => (
                      <td key={k} className="px-3 py-2 align-top">
                        <div className="text-[9px] text-ink-400 uppercase">{k}</div>
                        <div className="font-mono tabular text-[11px]">
                          {v == null ? '—' : Array.isArray(v) ? v.join(', ') : String(v)}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {summary.skippedRows?.length > 0 && (
        <details className="rounded-xl ring-1 ring-amber-300 dark:ring-amber-700 p-3">
          <summary className="text-sm cursor-pointer font-semibold text-amber-700 dark:text-amber-300">
            عرض السطور المتجاهلة ({summary.skippedRows.length})
          </summary>
          <ul className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">
            {summary.skippedRows.slice(0, 30).map((r, i) => (
              <li key={i} className="text-ink-500">
                صف {r.row}: {r.reason}
              </li>
            ))}
            {summary.skippedRows.length > 30 && (
              <li className="text-ink-400 italic">… وأكثر</li>
            )}
          </ul>
        </details>
      )}

      <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-ink-100 dark:border-ink-800">
        <Btn variant="ghost" onClick={onCancel} disabled={confirming}>
          إلغاء
        </Btn>
        <Btn
          variant="primary"
          icon="check"
          onClick={onConfirm}
          loading={confirming}
          disabled={ins + upd + auto === 0}
        >
          تأكيد الحفظ ({ins + upd + auto})
        </Btn>
      </div>
    </div>
  );
}

function ResultStat({ label, value, tone }) {
  const tones = {
    neutral: 'text-ink-700 dark:text-ink-200',
    success: 'text-emerald-600',
    info: 'text-sky-600',
    warn: 'text-amber-600',
  };
  return (
    <div className="p-3 rounded-xl bg-ink-50 dark:bg-ink-800/40">
      <div className="text-[10px] uppercase text-ink-400 font-bold">{label}</div>
      <div className={`text-2xl font-black tabular mt-0.5 ${tones[tone]}`}>{fmtAR(value)}</div>
    </div>
  );
}
